/**
 * /redeem — unified redemption surface (OCP-159 + OCP-264).
 *
 * Public route. The page renders one of six UI states depending on
 * the combination of auth state, email-verified state, and whether a
 * `?code=...` query param is present:
 *
 *   - loading                  → spinner (auth-fetch in flight)
 *   - unauth + ?code=          → chooser: Sign up / Log in (Phase B core)
 *   - unauth + no code         → "sign in to redeem" + login link
 *   - auth + !verified + code  → "verify your email to claim ORB-XXX"
 *                                + resend button (code parked in
 *                                sessionStorage so /verify-email can
 *                                bounce back here post-verify)
 *   - auth + verified + code   → confirm panel: subdomain input + claim
 *                                button (POST /v1/portal/codes/redeem)
 *   - auth + verified + no code→ free-text "Enter code" form
 *                                (OCP-159 preserved)
 *
 * On a successful redeem the page swaps to a per-outcome SuccessPanel
 * (subscription | credits | instance).
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import {
  clearPendingRedeemCode,
  setPendingRedeemCode,
} from '../lib/redeem-pending';
import { OrbotoLockup } from '../components/OrbotoLockup';
import { useResponsibleContactGate } from '../components/ResponsibleContactGate';
import { InstanceProvisioningSuccess } from '../components/InstanceProvisioningSuccess';
import { RoleReadOnlyNotice } from '../components/RoleReadOnlyNotice';
import {
  BUSINESS_CAPACITY_PATH,
  BusinessCapacityForm,
  BusinessCapacityRefusalNotice,
  canDeclareBusinessCapacity,
  contractGateMessage,
  type BusinessCapacityResponse,
} from '../components/BusinessCapacityGate';
import { usePortalAccess } from '../lib/use-portal-access';
import { fmtDate } from '../lib/format-time';
import { fmtNumber } from '../lib/format-money';
import { LegalFooter } from '../components/LegalFooter';

interface SubscriptionResult {
  outcome: 'subscription';
  product_id: string;
  product_display_name: string;
  subscription_expires_at: string;
}
interface CreditsResult {
  outcome: 'credits';
  product_id: string;
  product_display_name: string;
  credits_added: number;
}
interface InstanceResult {
  outcome: 'instance';
  tenant_id: string;
  subdomain: string;
  magic_login_url: string;
  tier_recommendation?: {
    suggested_tier: 'team' | 'business';
    contact_sales: boolean;
    show_upgrade_prompt: boolean;
    reason: string;
  };
}
type RedeemResult = SubscriptionResult | CreditsResult | InstanceResult;

const ERROR_CODES = [
  'code_unknown',
  'code_expired',
  'code_exhausted',
  'code_already_redeemed_by_customer',
  'product_not_found',
  'mail_subscription_already_active',
  'subdomain_required',
  'reserved_subdomain',
  'subdomain_taken',
  'invalid_team_size',
  // OCP-847 - AGB and AVV are both mandatory: with either unpublished no contract is concluded.
  'terms_not_published',
  'avv_not_published',
] as const;

/** The customer-facing copy for a redeem refusal code; null for an unknown code. */
function errorCopy(t: TFunction<'redeem'>, code: string): string | null {
  return (ERROR_CODES as readonly string[]).includes(code) ? t(`errorCopy.${code}`) : null;
}

const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;

export function RedeemPage() {
  const { t } = useTranslation('redeem');
  const { canConcludeContract } = usePortalAccess();
  const { status, user } = useAuth();
  const [params] = useSearchParams();
  const codeFromUrl = (params.get('code') ?? '').trim();
  const [successResult, setSuccessResult] = useState<RedeemResult | null>(null);

  // Auth-fetch in flight — show spinner so we don't briefly flash the
  // unauth chooser before /v1/portal/auth/me resolves.
  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="text-sm text-gray-500">{t('loading')}</div>
      </div>
    );
  }

  // Successful redeem - keep the SuccessPanel mounted regardless of
  // surrounding chrome.
  if (successResult) {
    return (
      <StandaloneChrome>
        <SuccessPanel result={successResult} />
      </StandaloneChrome>
    );
  }

  // ---- Unauthenticated -----------------------------------------------

  if (status === 'unauthenticated') {
    return (
      <StandaloneChrome>
        {codeFromUrl ? (
          <AuthChooser code={codeFromUrl} />
        ) : (
          <SignInPrompt />
        )}
      </StandaloneChrome>
    );
  }

  // ---- Authenticated but email not verified --------------------------

  if (status === 'authenticated' && user && !user.emailVerified) {
    return (
      <StandaloneChrome>
        <VerifyEmailFirst code={codeFromUrl} email={user.email} />
      </StandaloneChrome>
    );
  }

  // ---- Authenticated + verified --------------------------------------

  // OCP-570 - redeeming concludes a contract for the account: owner/admin
  // only (the API answers 403 contract_requires_admin otherwise).
  if (!canConcludeContract) {
    return (
      <StandaloneChrome>
        <RoleReadOnlyNotice scope="contract" />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t('roleGate')}
        </p>
      </StandaloneChrome>
    );
  }

  return (
    <StandaloneChrome>
      {codeFromUrl ? (
        <ConfirmRedeemGate
          code={codeFromUrl}
          onSuccess={(r) => {
            clearPendingRedeemCode();
            setSuccessResult(r);
          }}
        />
      ) : (
        <FreeFormRedeem onSuccess={setSuccessResult} />
      )}
    </StandaloneChrome>
  );
}

// ---- Sub-components ---------------------------------------------------

function StandaloneChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-6 py-4">
        <Link to="/" className="inline-flex items-center gap-2">
          <OrbotoLockup className="h-6 w-auto" />
        </Link>
      </header>
      <main className="flex flex-1 items-start justify-center px-6 py-12">
        <div className="w-full max-w-xl">{children}</div>
      </main>
      <LegalFooter className="pb-6" />
    </div>
  );
}

function AuthChooser({ code }: { code: string }) {
  const { t } = useTranslation('redeem');
  const encodedReturn = encodeURIComponent(`/redeem?code=${encodeURIComponent(code)}`);
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500">
        {t('kicker')}
      </p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight">{t('chooser.title')}</h1>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        {t('chooser.intro')}
      </p>

      <div className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-gray-500">{t('chooser.yourCode')}</div>
        <div className="mt-1 font-mono text-lg uppercase tracking-wider">{code}</div>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row">
        <Link
          to={`/signup?from=${encodedReturn}`}
          className="flex-1 rounded bg-brand-500 px-4 py-3 text-center text-sm font-medium text-white hover:bg-brand-600"
          data-testid="redeem-chooser-signup"
        >
          {t('chooser.signup')}
        </Link>
        <Link
          to={`/login?from=${encodedReturn}`}
          className="flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-center text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800/60"
          data-testid="redeem-chooser-login"
        >
          {t('chooser.login')}
        </Link>
      </div>

      <p className="mt-6 text-xs text-gray-500">
        {t('chooser.footnote')}
      </p>
    </div>
  );
}

function SignInPrompt() {
  const { t } = useTranslation('redeem');
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{t('signInPrompt.title')}</h1>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        {t('signInPrompt.intro')}
      </p>
      <Link
        to="/login?from=%2Fredeem"
        className="mt-6 inline-block rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        {t('signInPrompt.cta')}
      </Link>
    </div>
  );
}

function VerifyEmailFirst({ code, email }: { code: string; email: string }) {
  const { t } = useTranslation('redeem');
  const [resendStatus, setResendStatus] = useState<
    null | 'pending' | 'sent' | 'rate_limited' | 'failed'
  >(null);

  // Park the code so /verify-email can route the user back here after
  // they click the link from their inbox.
  useEffect(() => {
    if (code) {
      setPendingRedeemCode(code);
    } else {
      clearPendingRedeemCode();
    }
  }, [code]);

  async function resend() {
    if (resendStatus === 'pending') return;
    setResendStatus('pending');
    try {
      await api('/v1/portal/auth/email-verification/resend', {
        method: 'POST',
        body: { email },
      });
      setResendStatus('sent');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setResendStatus('rate_limited');
      } else {
        setResendStatus('failed');
      }
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{t('verifyFirst.title')}</h1>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        <Trans t={t} i18nKey="verifyFirst.sentTo" values={{ email }} components={{ email: <span className="font-medium" /> }} />
        {code ? (
          <Trans t={t} i18nKey="verifyFirst.withCode" values={{ code }} components={{ code: <span className="font-mono uppercase" /> }} />
        ) : (
          t('verifyFirst.withoutCode')
        )}
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={resend}
          disabled={resendStatus === 'pending'}
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {resendStatus === 'pending' ? t('verifyFirst.sending') : t('verifyFirst.resend')}
        </button>
        <Link
          to="/"
          className="rounded px-4 py-2 text-sm text-gray-600 hover:text-gray-900 dark:hover:text-gray-100"
        >
          {t('verifyFirst.skip')}
        </Link>
      </div>

      {resendStatus === 'sent' && (
        <p className="mt-4 rounded border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
          {t('verifyFirst.sent')}
        </p>
      )}
      {resendStatus === 'rate_limited' && (
        <p className="mt-4 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
          {t('verifyFirst.rateLimited')}
        </p>
      )}
      {resendStatus === 'failed' && (
        <p className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {t('verifyFirst.failed')}
        </p>
      )}
    </div>
  );
}

interface RedemptionStatusResponse {
  redeemed: boolean;
  tenant_id: string | null;
  subdomain: string | null;
}

/**
 * Re-entry guard (OCP-461). Before showing the redeem form for a code
 * in the URL, ask the server whether this customer already consumed it.
 * If they did:
 *   - and it provisioned an instance they own -> show that instance's
 *     live provisioning/ready state (reuses OCP-457's panel), NOT a
 *     fresh form.
 *   - otherwise (a consumed Mail/credits code) -> show an
 *     already-redeemed acknowledgement, NOT a fresh form.
 * A not-yet-redeemed code falls through to the normal confirm form.
 *
 * Fails open: any error checking the status renders the form so a real
 * redemption is never blocked by a status blip (the redeem POST itself
 * remains the source of truth + backstop).
 */
function ConfirmRedeemGate({
  code,
  onSuccess,
}: {
  code: string;
  onSuccess: (r: RedeemResult) => void;
}) {
  const { t } = useTranslation('redeem');
  const [state, setState] = useState<
    | { kind: 'checking' }
    | { kind: 'form' }
    | { kind: 'reentry-instance'; tenantId: string; subdomain: string }
    | { kind: 'reentry-other' }
  >({ kind: 'checking' });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'checking' });
    (async () => {
      try {
        const r = await api<RedemptionStatusResponse>(
          `/v1/portal/codes/redemption-status?code=${encodeURIComponent(code)}`,
        );
        if (cancelled) return;
        if (r.redeemed && r.tenant_id && r.subdomain) {
          setState({
            kind: 'reentry-instance',
            tenantId: r.tenant_id,
            subdomain: r.subdomain,
          });
        } else if (r.redeemed) {
          setState({ kind: 'reentry-other' });
        } else {
          setState({ kind: 'form' });
        }
      } catch {
        // Fail open - never block a real redemption on a status blip.
        if (!cancelled) setState({ kind: 'form' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (state.kind === 'checking') {
    return (
      <div
        className="flex items-center justify-center py-16 text-sm text-gray-500"
        data-testid="redeem-reentry-checking"
      >
        {t('reentry.checking')}
      </div>
    );
  }

  if (state.kind === 'reentry-instance') {
    // Re-derive the instance's live provisioning/ready state. No fresh
    // magic-login token on re-entry -> the ready CTA links to My
    // Instances (handled by InstanceProvisioningSuccess).
    return (
      <div data-testid="redeem-reentry-instance">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500">
          {t('kicker')}
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight">
          {t('reentry.instanceTitle')}
        </h1>
        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          {t('reentry.instanceIntro')}
        </p>
        <div className="mt-6">
          <InstanceProvisioningSuccess
            tenantId={state.tenantId}
            subdomain={state.subdomain}
          />
        </div>
      </div>
    );
  }

  if (state.kind === 'reentry-other') {
    return <AlreadyRedeemedPanel />;
  }

  return <ConfirmRedeem code={code} onSuccess={onSuccess} />;
}

function AlreadyRedeemedPanel() {
  const { t } = useTranslation('redeem');
  return (
    <div data-testid="redeem-reentry-other">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500">
        {t('kicker')}
      </p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight">
        {t('reentry.otherTitle')}
      </h1>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        {t('reentry.otherIntro')}
      </p>
      <Link
        to="/"
        className="mt-6 inline-block rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
      >
        {t('reentry.goToAccount')}
      </Link>
    </div>
  );
}

type SubdomainAvailability =
  | { kind: 'idle' }
  | { kind: 'invalid' }
  | { kind: 'checking' }
  | { kind: 'available' }
  | { kind: 'taken' }
  | { kind: 'reserved' }
  | { kind: 'error' };

interface SubdomainCheckResponse {
  available: boolean;
  reason?: 'invalid_format' | 'reserved' | 'taken';
}

export function ConfirmRedeem({
  code,
  onSuccess,
  availabilityDebounceMs = 400,
}: {
  code: string;
  onSuccess: (r: RedeemResult) => void;
  /** Debounce before the live availability check fires. Tests shorten it. */
  availabilityDebounceMs?: number;
}) {
  const { t } = useTranslation('redeem');
  const [subdomain, setSubdomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ code: string; message: string; gate: boolean } | null>(null);
  const [avail, setAvail] = useState<SubdomainAvailability>({ kind: 'idle' });
  const contactGate = useResponsibleContactGate();
  // OCP-623 - the Sec. 14 BGB business-capacity declaration (OCP-534, Terms
  // s. 1 (2)) that POST /codes/redeem gates on. A redeem-first signup lands
  // here with nothing on file, so the declaration is collected INLINE with
  // the same form the order dialog uses; the company name comes from the
  // declaration (one source of truth), never from a free-text field. A failed
  // probe keeps the old behaviour (Claim enabled, the API gate answers).
  const [capacity, setCapacity] = useState<BusinessCapacityResponse | null>(null);
  const [capacityProbe, setCapacityProbe] = useState<'loading' | 'ok' | 'failed'>('loading');
  const [declarationDismissed, setDeclarationDismissed] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    void (async () => {
      try {
        const r = await api<BusinessCapacityResponse>(BUSINESS_CAPACITY_PATH, { signal: ac.signal });
        setCapacity(r);
        setCapacityProbe('ok');
      } catch {
        if (!ac.signal.aborted) setCapacityProbe('failed');
      }
    })();
    return () => ac.abort();
  }, []);

  const capacityBlocked = capacity?.blocked_reason ?? null;
  const needsDeclaration =
    capacityBlocked === 'business_capacity_missing' || capacityBlocked === 'company_name_missing';
  const capacityRefused = capacityBlocked === 'business_capacity_refused';
  const canDeclare = canDeclareBusinessCapacity(capacity?.role);
  const capacityBlocksSubmit = capacityProbe === 'ok' && capacity !== null && !capacity.can_conclude;

  // Live, debounced availability feedback (OCP-461). Local format gate
  // first (so we don't call the API for shapes the redeem endpoint would
  // reject anyway), then the shared public availability endpoint for the
  // taken/reserved/available answer.
  useEffect(() => {
    const value = subdomain.trim().toLowerCase();
    if (!value) {
      setAvail({ kind: 'idle' });
      return;
    }
    // Mirror the redeem endpoint's own gate (3-40 chars, RFC-1123 shape)
    // so a name the server would reject never shows "available".
    if (value.length < 3 || value.length > 40 || !SUBDOMAIN_PATTERN.test(value)) {
      setAvail({ kind: 'invalid' });
      return;
    }
    let cancelled = false;
    setAvail({ kind: 'checking' });
    const timer = setTimeout(async () => {
      try {
        const r = await api<SubdomainCheckResponse>('/v1/subdomain-available', {
          method: 'POST',
          body: { name: value },
        });
        if (cancelled) return;
        if (r.available) setAvail({ kind: 'available' });
        else if (r.reason === 'taken') setAvail({ kind: 'taken' });
        else if (r.reason === 'reserved') setAvail({ kind: 'reserved' });
        else setAvail({ kind: 'invalid' });
      } catch {
        // Soft-fail: don't block submit on a check error; the on-submit
        // subdomain_taken response is the race/edge backstop.
        if (!cancelled) setAvail({ kind: 'error' });
      }
    }, availabilityDebounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [subdomain, availabilityDebounceMs]);

  // Only a non-empty subdomain gates submit (subdomain is optional for
  // Mail/credits codes, which ignore the field entirely).
  const availBlocksSubmit =
    Boolean(subdomain.trim()) &&
    (avail.kind === 'checking' ||
      avail.kind === 'taken' ||
      avail.kind === 'reserved' ||
      avail.kind === 'invalid');

  async function runRedeem() {
    setError(null);
    setSubmitting(true);
    try {
      const body: Record<string, string> = { code };
      if (subdomain.trim()) body.subdomain = subdomain.trim().toLowerCase();
      // The dispatcher's company label is the declared company name (OCP-623).
      const company = capacity?.company_name?.trim();
      if (company) body.company = company;
      const r = await api<RedeemResult>('/v1/portal/codes/redeem', {
        method: 'POST',
        body,
      });
      onSuccess(r);
    } catch (err) {
      if (err instanceof ApiError) {
        const reason =
          err.body && typeof err.body === 'object' && 'error' in err.body
            ? String((err.body as { error: unknown }).error)
            : `http_${err.status}`;
        // OCP-623 - a Terms gate (declaration / company / refusal / contact)
        // explains itself and points at Settings instead of "Could not redeem".
        const gate = contractGateMessage(err);
        setError(
          gate
            ? { code: reason, message: gate, gate: true }
            : { code: reason, message: errorCopy(t, reason) ?? t('confirm.unknownError', { reason }), gate: false },
        );
      } else {
        setError({ code: 'network_error', message: t('confirm.networkError'), gate: false });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // OCP-524 - a redemption concludes a contract, so the Terms require a named
  // responsible contact person first. Same gate + same wording as the checkout
  // entry points; invisible once the account has named someone.
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    await contactGate.ensure(runRedeem);
  }

  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-500">
        {t('kicker')}
      </p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight">{t('confirm.title')}</h1>
      <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
        {t('confirm.intro')}
      </p>

      <div className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-3 shadow-sm">
        <div className="text-xs uppercase tracking-wide text-gray-500">{t('confirm.yourCode')}</div>
        <div className="mt-1 font-mono text-lg uppercase tracking-wider">{code}</div>
      </div>

      {/* OCP-623 - the Sec. 14 BGB declaration, collected here when the account
          has none yet (a redeem-first signup always lands like this). Same form
          as the order dialog and Settings, so the wording cannot drift. */}
      {needsDeclaration && canDeclare && declarationDismissed && (
        <div
          role="alert"
          className="mt-6 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-3 text-sm text-amber-900 dark:text-amber-200"
          data-testid="redeem-business-capacity-dismissed"
        >
          <Trans
            t={t}
            i18nKey="confirm.dismissed"
            components={{
              confirm: <button type="button" className="underline" onClick={() => setDeclarationDismissed(false)} />,
              settings: <Link to="/settings" className="underline" />,
            }}
          />
        </div>
      )}
      {needsDeclaration && canDeclare && !declarationDismissed && (
        <div
          className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
          data-testid="redeem-business-capacity"
        >
          <h2 className="text-base font-semibold">{t('confirm.beforeYouClaim')}</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('confirm.declarationIntro')}
          </p>
          <BusinessCapacityForm
            initial={capacity}
            submitLabel={t('confirm.declarationSubmit')}
            cancelLabel={t('confirm.declarationCancel')}
            onCancel={() => setDeclarationDismissed(true)}
            onSaved={(bc) => {
              setCapacity(bc);
              setError(null);
            }}
          />
        </div>
      )}
      {needsDeclaration && !canDeclare && (
        <div
          role="alert"
          className="mt-6 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-3 text-sm text-amber-900 dark:text-amber-200"
          data-testid="redeem-business-capacity-role"
        >
          <Trans t={t} i18nKey="confirm.roleCannotDeclare" components={{ settings: <Link to="/settings" className="underline" /> }} />
        </div>
      )}
      {capacityRefused && <BusinessCapacityRefusalNotice refusal={capacity?.refusal ?? null} />}

      <form
        onSubmit={submit}
        className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            {t('confirm.subdomain')} <span className="text-gray-500">{t('confirm.subdomainHint')}</span>
          </span>
          <div className="flex items-stretch overflow-hidden rounded border border-gray-300 dark:border-gray-700 focus-within:border-brand-500">
            <input
              type="text"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
              placeholder={t('confirm.subdomainPlaceholder')}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="flex-1 bg-transparent px-3 py-2 font-mono text-sm focus:outline-none"
            />
            <span className="border-l border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-2 font-mono text-sm text-gray-500">
              .orbo.to
            </span>
          </div>
          <SubdomainAvailabilityHint
            state={avail}
            subdomain={subdomain.trim().toLowerCase()}
          />
        </label>

        {/* OCP-623 - the company on the instance is the declared one; edit it
            under Settings, not in a second free-text field. */}
        {capacity?.company_name && (
          <div className="mt-4 text-sm" data-testid="redeem-company">
            <span className="text-gray-700 dark:text-gray-300">{t('confirm.company')}</span>
            <div className="mt-1 flex items-center justify-between gap-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2">
              <span>{capacity.company_name}</span>
              <Link to="/settings" className="text-xs text-brand-600 hover:underline">
                {t('confirm.changeInSettings')}
              </Link>
            </div>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mt-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            data-testid="redeem-error"
          >
            {error.message}
            {error.gate && (
              <>
                {' '}
                <Link to="/settings" className="underline">
                  {t('confirm.openSettings')}
                </Link>
              </>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || availBlocksSubmit || capacityBlocksSubmit}
          className="mt-5 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {submitting ? t('confirm.claiming') : t('confirm.claim')}
        </button>

        <p className="mt-3 text-xs text-gray-500">
          {t('confirm.footnote')}
        </p>
      </form>

      {contactGate.element}
    </div>
  );
}

function SubdomainAvailabilityHint({
  state,
  subdomain,
}: {
  state: SubdomainAvailability;
  subdomain: string;
}) {
  const { t } = useTranslation('redeem');
  if (state.kind === 'idle') return null;

  const base = 'mt-1 flex items-center gap-1.5 text-xs';
  const spinner = (
    <span
      aria-hidden
      className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );

  const content: { cls: string; node: React.ReactNode } = (() => {
    switch (state.kind) {
      case 'checking':
        return {
          cls: 'text-gray-500',
          node: (
            <>
              {spinner}
              <span>{t('availability.checking')}</span>
            </>
          ),
        };
      case 'available':
        return {
          cls: 'text-emerald-600 dark:text-emerald-400',
          node: (
            <>
              <span aria-hidden>&#10003;</span>
              <span>
                <Trans t={t} i18nKey="availability.available" values={{ host: `${subdomain}.orbo.to` }} components={{ host: <span className="font-mono" /> }} />
              </span>
            </>
          ),
        };
      case 'taken':
        return {
          cls: 'text-red-600 dark:text-red-400',
          node: <span>{t('availability.taken')}</span>,
        };
      case 'reserved':
        return {
          cls: 'text-red-600 dark:text-red-400',
          node: <span>{t('availability.reserved')}</span>,
        };
      case 'invalid':
        return {
          cls: 'text-red-600 dark:text-red-400',
          node: (
            <span>
              {t('availability.invalid')}
            </span>
          ),
        };
      case 'error':
        return {
          cls: 'text-amber-600 dark:text-amber-400',
          node: (
            <span>
              {t('availability.error')}
            </span>
          ),
        };
      default:
        return { cls: 'text-gray-500', node: null };
    }
  })();

  return (
    <span
      className={`${base} ${content.cls}`}
      data-testid="subdomain-availability"
      data-state={state.kind}
      aria-live="polite"
    >
      {content.node}
    </span>
  );
}

function FreeFormRedeem({ onSuccess }: { onSuccess: (r: RedeemResult) => void }) {
  const { t } = useTranslation('redeem');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contactGate = useResponsibleContactGate();

  async function runRedeem() {
    setError(null);
    setSubmitting(true);
    try {
      const r = await api<RedeemResult>('/v1/portal/codes/redeem', {
        method: 'POST',
        body: { code: code.trim() },
      });
      onSuccess(r);
    } catch (err) {
      if (err instanceof ApiError) {
        const reason =
          err.body && typeof err.body === 'object' && 'error' in err.body
            ? String((err.body as { error: unknown }).error)
            : `http_${err.status}`;
        setError(reason);
      } else {
        setError('network_error');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // OCP-524 - see ConfirmRedeem: a redemption is a contract conclusion.
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting || !code.trim()) return;
    await contactGate.ensure(runRedeem);
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">{t('freeForm.title')}</h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {t('freeForm.intro')}
      </p>

      <form
        onSubmit={submit}
        className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-gray-700 dark:text-gray-300">{t('freeForm.code')}</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t('freeForm.placeholder')}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-2 font-mono text-sm uppercase tracking-wider focus:border-brand-500 focus:outline-none"
          />
        </label>

        {error === 'subdomain_required' && (
          <div
            role="alert"
            className="mt-3 rounded border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
          >
            {t('freeForm.subdomainRequired')}
          </div>
        )}
        {error && error !== 'subdomain_required' && (
          <div
            role="alert"
            className="mt-3 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            {errorCopy(t, error) ?? t('confirm.unknownError', { reason: error })}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !code.trim()}
          className="mt-4 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {submitting ? t('freeForm.submitting') : t('freeForm.submit')}
        </button>
      </form>

      {contactGate.element}
    </div>
  );
}

function SuccessPanel({ result }: { result: RedeemResult }) {
  const { t } = useTranslation('redeem');
  if (result.outcome === 'instance') {
    // OCP-457: the CTA + magic-login link stay gated (disabled + a
    // "still provisioning" spinner) until the tenant is active AND
    // reachable, so a brand-new customer never clicks into a cert error
    // or a 502. Live progress + terminal detection reuse the OCP-205
    // lifecycle polling mechanism.
    return (
      <InstanceProvisioningSuccess
        tenantId={result.tenant_id}
        subdomain={result.subdomain}
        magicLoginUrl={result.magic_login_url}
      />
    );
  }

  if (result.outcome === 'subscription') {
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 p-6">
        <h2 className="text-lg font-semibold text-emerald-900 dark:text-emerald-200">
          {t('success.subscriptionTitle')}
        </h2>
        <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-300">
          <Trans
            t={t}
            i18nKey="success.subscriptionBody"
            values={{ product: result.product_display_name, date: fmtDate(result.subscription_expires_at) }}
            components={{ product: <span className="font-medium" />, date: <span className="font-medium" /> }}
          />
        </p>
        <Link
          to="/subscriptions"
          className="mt-4 inline-block rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          {t('success.viewSubscriptions')}
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-900/30 p-6">
      <h2 className="text-lg font-semibold text-emerald-900 dark:text-emerald-200">
        {t('success.creditsTitle')}
      </h2>
      <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-300">
        <Trans
          t={t}
          i18nKey="success.creditsBody"
          values={{ product: result.product_display_name, sends: fmtNumber(result.credits_added) }}
          components={{ product: <span className="font-medium" />, count: <span className="font-medium" /> }}
        />
      </p>
      <Link
        to="/mail"
        className="mt-4 inline-block rounded bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
      >
        {t('success.viewMail')}
      </Link>
    </div>
  );
}
